// ============================================================================
// MarketWalletTopUp — пополнение кошелька мастера через существующий
// платёжный слой (PaymentService). Всё инлайн: без модальных окон платформы.
//
// Состояния явные: нет провайдеров / ошибка загрузки способов / нет связи /
// создаётся / успех (ссылка на оплату или счёт) / ошибка создания платежа.
// Сумма вводится в рублях, в платёжный слой уходит в копейках (как «Мой счёт»).
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, CreditCard, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatValue } from '@/lib/format';
import {
  createMarketWalletTopUp,
  rubToMinor,
  type MarketWalletProvider,
} from '@/lib/marketWallet';

const PRESETS = [500, 1000, 2000, 5000];

type ResultState =
  | { kind: 'success'; title: string; text: string; url?: string }
  | { kind: 'error'; title: string; text: string };

function money(value: number): string {
  return formatValue(value, 'number:0;group', 'numeric');
}

interface MarketWalletTopUpProps {
  providers: MarketWalletProvider[];
  providersError: string;
  disabled: boolean;
  currency: string;
  userId: string;
  /** paid=true — платёж сразу оплачен (mock), нужно перечитать кошелёк. */
  onDone: (paid: boolean) => void;
}

export function MarketWalletTopUp({
  providers,
  providersError,
  disabled,
  currency,
  userId,
  onDone,
}: MarketWalletTopUpProps) {
  const [provider, setProvider] = useState('');
  const [amount, setAmount] = useState('500');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ResultState | null>(null);

  useEffect(() => {
    if (!provider && providers.length > 0) setProvider(providers[0].code);
  }, [provider, providers]);

  const parsedAmount = useMemo(() => {
    const normalized = amount.replace(',', '.').trim();
    if (!normalized) return 0;
    const value = Number(normalized);
    return Number.isFinite(value) ? value : 0;
  }, [amount]);
  const minor = rubToMinor(parsedAmount);

  if (disabled) {
    return (
      <section id="market-wallet-topup" className="scroll-mt-4">
        <h2 className="mb-2 text-sm font-semibold">Пополнить счёт</h2>
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-3.5 text-xs leading-5 text-amber-800">
          Нет связи с сервером. Пополнение станет доступно после восстановления подключения.
        </div>
      </section>
    );
  }

  if (providersError) {
    return (
      <section id="market-wallet-topup" className="scroll-mt-4">
        <h2 className="mb-2 text-sm font-semibold">Пополнить счёт</h2>
        <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-3.5">
          <div className="flex items-start gap-2 text-xs leading-5 text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="min-w-0 flex-1 break-words">
              <div className="font-medium">Пополнение недоступно</div>
              <div>{providersError}</div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (providers.length === 0) {
    return (
      <section id="market-wallet-topup" className="scroll-mt-4">
        <h2 className="mb-2 text-sm font-semibold">Пополнить счёт</h2>
        <div className="rounded-2xl border border-dashed bg-card/50 px-3.5 py-3 text-xs leading-5 text-muted-foreground">
          Способы пополнения не настроены. Администратор базы подключает их
          в разделе «Биллинг → Платежи».
        </div>
      </section>
    );
  }

  const submit = async () => {
    if (!provider) {
      setResult({ kind: 'error', title: 'Выберите способ', text: 'Укажите способ пополнения.' });
      return;
    }
    if (!minor) {
      setResult({ kind: 'error', title: 'Проверьте сумму', text: 'Сумма должна быть больше нуля.' });
      return;
    }
    setBusy(true);
    setResult(null);
    const response = await createMarketWalletTopUp(provider, parsedAmount, userId);
    if (!response.ok) {
      setResult({ kind: 'error', title: 'Платёж не создан', text: response.message });
    } else if (response.paid) {
      setResult({
        kind: 'success',
        title: 'Счёт пополнен',
        text: 'Платёж прошёл, баланс обновлён.',
      });
      onDone(true);
    } else if (response.confirmationUrl) {
      setResult({
        kind: 'success',
        title: 'Платёж создан',
        text: 'Откройте страницу оплаты и завершите платёж. Затем вернитесь и обновите кошелёк.',
        url: response.confirmationUrl,
      });
    } else {
      setResult({
        kind: 'success',
        title: 'Счёт на оплату создан',
        text: 'Оплатите по реквизитам — зачисление произойдёт автоматически. Затем обновите кошелёк.',
      });
    }
    setBusy(false);
  };

  const clearResult = () => {
    if (result) setResult(null);
  };

  return (
    <section id="market-wallet-topup" className="scroll-mt-4">
      <h2 className="mb-2 text-sm font-semibold">Пополнить счёт</h2>
      <div className="space-y-3 rounded-2xl border bg-card p-3.5 shadow-sm">
        <div>
          <div className="mb-1.5 text-[11px] font-medium text-muted-foreground">Сумма</div>
          <div className="flex flex-wrap gap-1.5">
            {PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => {
                  setAmount(String(preset));
                  clearResult();
                }}
                className={`min-h-8 rounded-lg border px-2.5 text-xs font-medium tabular-nums transition-colors ${
                  parsedAmount === preset
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'bg-background hover:bg-accent/40'
                }`}
              >
                {money(preset)}
              </button>
            ))}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <input
              inputMode="decimal"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                clearResult();
              }}
              className="h-10 w-full rounded-xl border border-border bg-background px-3 text-sm tabular-nums outline-none focus:border-primary"
              placeholder="Сумма"
              aria-label="Сумма пополнения"
            />
            <span className="shrink-0 text-xs text-muted-foreground">{currency || 'руб.'}</span>
          </div>
        </div>

        <div>
          <div className="mb-1.5 text-[11px] font-medium text-muted-foreground">Способ оплаты</div>
          <div className="flex flex-wrap gap-1.5">
            {providers.map((p) => (
              <button
                key={p.code}
                type="button"
                onClick={() => {
                  setProvider(p.code);
                  clearResult();
                }}
                className={`flex min-h-9 items-center gap-1.5 rounded-xl border px-3 text-xs font-medium transition-colors ${
                  provider === p.code
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'bg-background hover:bg-accent/40'
                }`}
              >
                <CreditCard className="h-3.5 w-3.5" />
                {p.title}
              </button>
            ))}
          </div>
        </div>

        <Button
          className="h-11 w-full rounded-xl"
          onClick={() => void submit()}
          disabled={busy}
        >
          {busy ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Создаём платёж…
            </>
          ) : (
            `Пополнить${minor ? ` на ${money(parsedAmount)} ${currency || 'руб.'}` : ''}`
          )}
        </Button>

        {result?.kind === 'success' && (
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3">
            <div className="flex items-start gap-2 text-xs leading-5 text-emerald-800">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-medium">{result.title}</div>
                <div className="mt-0.5 break-words">{result.text}</div>
                {result.url && (
                  <a
                    href={result.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex h-8 items-center rounded-lg bg-emerald-600 px-3 text-xs font-medium text-white"
                  >
                    Перейти к оплате
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {result?.kind === 'error' && (
          <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-3">
            <div className="flex items-start gap-2 text-xs leading-5 text-destructive">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-medium">{result.title}</div>
                <div className="mt-0.5 break-words">{result.text}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
